/**
 * "Run this?" — the first-run approval for a repo's launch configuration.
 *
 * A committed `.apiboo/launch.json` (or `.claude/launch.json`) is code from
 * whoever wrote the repo. Before Apiboo runs a configuration for the first
 * time it shows the full command and asks; it asks again whenever the
 * command changes. What counts as "the command" is its fingerprint: the
 * program, every argument, the cwd and every env NAME and VALUE. Values are
 * in on purpose — `NODE_OPTIONS=--require ./x.js` changes what runs without
 * touching the program or its arguments. Only the fingerprint is stored,
 * never the values themselves.
 *
 * The fingerprint is SHA-256 (WebCrypto): the repo's author knows the
 * approved command and controls every byte of the next one, so a weak hash
 * could be steered into a collision. Without WebCrypto nothing counts as
 * approved — the dialog simply always asks.
 *
 * Approvals are a security decision, so they belong to the account
 * (`apibooKey`, unreachable from another account on the same device) and
 * live only on this device — never synced, never in the repo.
 */
import { apibooKey } from '../../../services/storageNamespace';
import { runKey } from './runKeys';

const STORE_SUFFIX = 'run-approvals';
const MAX_ENTRIES = 500;

async function sha256Hex(text) {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (!subtle) return null;
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** The fingerprint of what a configuration would run (null without WebCrypto). */
export async function commandFingerprint(config) {
  const env = Object.entries(config?.env || {})
    .map(([k, v]) => [k, String(v)])
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return sha256Hex(JSON.stringify([config?.runtimeExecutable || '', config?.runtimeArgs || [], config?.cwd || '', env]));
}

/** The command line as the confirm dialog shows it. Arguments with spaces are quoted. */
export function commandLine(config) {
  const quote = (a) => (/[\s"]/.test(a) || a === '' ? JSON.stringify(a) : a);
  return [config?.runtimeExecutable || '', ...(config?.runtimeArgs || [])].map(quote).join(' ');
}

function read() {
  try {
    const raw = localStorage.getItem(apibooKey(STORE_SUFFIX));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export async function isApproved(repoPath, config) {
  const fp = await commandFingerprint(config).catch(() => null);
  return !!fp && read()[runKey(repoPath, config.name)] === fp;
}

export async function approve(repoPath, config) {
  const fp = await commandFingerprint(config).catch(() => null);
  if (!fp) return;
  const all = read();
  const key = runKey(repoPath, config.name);
  delete all[key];
  all[key] = fp;
  // Oldest first (insertion order): drop the oldest beyond the cap.
  const keys = Object.keys(all);
  keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES)).forEach((k) => delete all[k]);
  try {
    localStorage.setItem(apibooKey(STORE_SUFFIX), JSON.stringify(all));
  } catch {
    // Storage full or blocked: the next run simply asks again.
  }
}
